import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const CUSTOMERS_API = `${API_BASE}/customers`;
const BOOKINGS_API = `${API_BASE}/bookings`;
const RATES_API = `${API_BASE}/rates`;

// Maps a master rate's name/unit to the fixed legacy slot the pricing engine
// (invoice.route.js, NewBooking.jsx) actually reads. Same matching this file
// already used in the old "Apply Standard Rates" button — kept so the
// existing pricing logic keeps working unchanged while the UI/data model
// underneath it becomes a general per-line override list instead of a fixed
// 10-box form.
function legacySlotFor(rate) {
  const name = String(rate.name || "").toLowerCase().trim();
  const unit = String(rate.unit || "").toLowerCase().trim();

  if (name === "cattle") return ["livestock", "cattlePerHead"];
  if (name === "sheep") return ["livestock", "sheepPerHead"];
  if (name === "deer") return ["livestock", "deerPerHead"];
  if (name === "other livestock" || name === "other") return ["livestock", "otherPerHead"];

  if (name === "pallet") return ["freight", "pallet"];
  if (name === "container") return ["freight", "container"];
  if (name === "vehicle") return ["freight", "vehicle"];
  if (name === "loose item" || name === "loose_item") return ["freight", "looseItem"];

  if (unit === "per_kg") return ["freight", "perKg"];
  if (unit === "per_m3") return ["freight", "perM3"];

  return null;
}

// Builds the legacy fixed-shape rates object FROM the general override map,
// so invoice.route.js / NewBooking.jsx (which only ever read
// customer.rates.livestock.* / customer.rates.freight.*) keep working
// exactly as before, with zero changes needed on their end.
function deriveLegacyRates(standardRates, overrideMap) {
  const legacy = {
    rateSource: Object.keys(overrideMap).length > 0 ? "custom" : "standard",
    livestock: { cattlePerHead: 0, sheepPerHead: 0, deerPerHead: 0, otherPerHead: 0 },
    freight: { pallet: 0, container: 0, vehicle: 0, looseItem: 0, perKg: 0, perM3: 0 },
  };

  standardRates.forEach((rate) => {
    const slot = legacySlotFor(rate);
    if (!slot) return;
    const [section, key] = slot;
    const effectivePrice = overrideMap[rate._id] != null ? overrideMap[rate._id] : Number(rate.price || 0);
    legacy[section][key] = effectivePrice;
  });

  return legacy;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [standardRates, setStandardRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRates, setSavingRates] = useState(false);
  const [editingRates, setEditingRates] = useState(false);
  // { [rateId]: overridePrice } — only rates the customer actually has a
  // different price on live here; anything absent uses the master price.
  const [rateOverrideMap, setRateOverrideMap] = useState({});
  const [hiddenRateIds, setHiddenRateIds] = useState(new Set());
  const [selectedRateIds, setSelectedRateIds] = useState(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [savingHidden, setSavingHidden] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [creatingPortalLink, setCreatingPortalLink] = useState(false);
  const [portalSetupUrl, setPortalSetupUrl] = useState("");

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [customerRes, bookingRes, ratesRes] = await Promise.all([
        fetch(`${CUSTOMERS_API}/${id}`).catch(() => null),
        fetch(BOOKINGS_API).catch(() => null),
        fetch(RATES_API).catch(() => null),
      ]);

      let customerData = null;

      if (customerRes?.ok) {
        customerData = await customerRes.json();
      } else {
        const allRes = await fetch(CUSTOMERS_API);
        const allCustomers = await allRes.json();
        const list = Array.isArray(allCustomers) ? allCustomers : (allCustomers?.data || []);

        customerData = list.find((c) => c._id === id) || null;
      }

      const bookingData = bookingRes?.ok ? await bookingRes.json() : [];
      const ratesData = ratesRes?.ok ? await ratesRes.json() : [];

      const finalCustomer = customerData?.data || customerData || null;

      setCustomer(finalCustomer);
      setBookings(Array.isArray(bookingData) ? bookingData : (bookingData?.data || []));
      setStandardRates(Array.isArray(ratesData) ? ratesData : (ratesData?.data || []));

      const overrideMap = {};
      (finalCustomer?.rateOverrides || []).forEach((o) => {
        if (o.rate) overrideMap[o.rate] = o.price;
      });
      setRateOverrideMap(overrideMap);
      setHiddenRateIds(new Set((finalCustomer?.hiddenRateIds || []).map(String)));
    } catch (error) {
      console.error("Failed to load customer:", error);
      setCustomer(null);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const customerBookings = useMemo(() => {
    if (!customer) return [];

    return bookings.filter((booking) => {
      const bookingCustomerId =
        booking.customer?._id ||
        booking.customer ||
        booking.customerId ||
        booking.pricingSnapshot?.customerId;

      const bookingCustomerName = String(
        booking.customerName || ""
      ).toLowerCase();
      const companyName = String(customer.companyName || "").toLowerCase();
      const tradingName = String(customer.tradingName || "").toLowerCase();
      const contactName = String(customer.contactName || "").toLowerCase();

      return (
        String(bookingCustomerId) === String(customer._id) ||
        bookingCustomerName === companyName ||
        bookingCustomerName === tradingName ||
        bookingCustomerName === contactName
      );
    });
  }, [bookings, customer]);

  const totalRevenue = customerBookings.reduce((sum, booking) => {
    return (
      sum +
      Number(
        booking.pricingSnapshot?.calculatedTotal ||
          booking.totals?.estimatedRevenue ||
          0
      )
    );
  }, 0);

  const totalInvoiced = customerBookings.reduce((sum, b) => sum + Number(b.charges?.total || 0), 0);
  const totalPaid = customerBookings.reduce((sum, b) => sum + Number(b.invoice?.amountPaid || 0), 0);
  const totalOutstanding = customerBookings.reduce((sum, b) => sum + Number(b.invoice?.amountDue || 0), 0);
  const invoicedBookings = customerBookings.filter((b) => b.invoice?.invoiceNumber);
  const unpaidCount = customerBookings.filter(
    (b) => b.invoice?.invoiceStatus && b.invoice.invoiceStatus !== "Paid" && b.invoice.invoiceStatus !== "Not Invoiced"
  ).length;

  const livestockSummary = useMemo(() => {
    const speciesMap = {};
    let totalHead = 0;
    let fitCount = 0;
    let vetCertCount = 0;
    let consignmentCount = 0;

    customerBookings.forEach((booking) => {
      (booking.livestockItems || []).forEach((item) => {
        const species = item.livestockType || "other";
        const head = Number(item.headCount || 0);
        speciesMap[species] = (speciesMap[species] || 0) + head;
        totalHead += head;
        consignmentCount += 1;
        if (item.fitForTransport) fitCount += 1;
        if (item.vetCertificateUploaded) vetCertCount += 1;
      });
    });

    return {
      totalHead,
      speciesBreakdown: Object.entries(speciesMap).sort((a, b) => b[1] - a[1]),
      consignmentCount,
      fitCount,
      vetCertCount,
    };
  }, [customerBookings]);

  const updateRateOverride = (rateId, value) => {
    const trimmed = String(value).trim();
    setRateOverrideMap((prev) => {
      const next = { ...prev };
      if (trimmed === "") {
        delete next[rateId];
      } else {
        next[rateId] = Number(trimmed);
      }
      return next;
    });
  };

  const resetRateOverride = (rateId) => {
    setRateOverrideMap((prev) => {
      const next = { ...prev };
      delete next[rateId];
      return next;
    });
  };

  const resetAllRateOverrides = () => {
    setRateOverrideMap({});
  };

  const toggleRateSelected = (rateId) => {
    setSelectedRateIds((prev) => {
      const next = new Set(prev);
      next.has(rateId) ? next.delete(rateId) : next.add(rateId);
      return next;
    });
  };

  const hideSelectedRates = async () => {
    if (selectedRateIds.size === 0) return;
    try {
      setSavingHidden(true);
      const nextHidden = new Set([...hiddenRateIds, ...selectedRateIds]);
      const response = await fetch(`${CUSTOMERS_API}/${customer._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...customer, hiddenRateIds: [...nextHidden] }),
      });
      if (!response.ok) throw new Error(await response.text());
      const updatedCustomer = await response.json();
      setCustomer(updatedCustomer);
      setHiddenRateIds(new Set((updatedCustomer?.hiddenRateIds || []).map(String)));
      setSelectedRateIds(new Set());
    } catch (error) {
      console.error(error);
      alert("Failed to hide rates: " + error.message);
    } finally {
      setSavingHidden(false);
    }
  };

  const unhideRate = async (rateId) => {
    try {
      setSavingHidden(true);
      const nextHidden = [...hiddenRateIds].filter((id) => id !== rateId);
      const response = await fetch(`${CUSTOMERS_API}/${customer._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...customer, hiddenRateIds: nextHidden }),
      });
      if (!response.ok) throw new Error(await response.text());
      const updatedCustomer = await response.json();
      setCustomer(updatedCustomer);
      setHiddenRateIds(new Set((updatedCustomer?.hiddenRateIds || []).map(String)));
    } catch (error) {
      console.error(error);
      alert("Failed to unhide rate: " + error.message);
    } finally {
      setSavingHidden(false);
    }
  };

  const saveRates = async () => {
    try {
      setSavingRates(true);

      const rateOverrides = standardRates
        .filter((rate) => rateOverrideMap[rate._id] != null)
        .map((rate) => ({
          rate: rate._id,
          name: rate.name,
          type: rate.type,
          unit: rate.unit,
          price: rateOverrideMap[rate._id],
        }));

      const legacyRates = deriveLegacyRates(standardRates, rateOverrideMap);

      const response = await fetch(`${CUSTOMERS_API}/${customer._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...customer,
          rates: legacyRates,
          rateOverrides,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to update customer rates");
      }

      const updatedCustomer = await response.json();

      setCustomer(updatedCustomer);
      const overrideMap = {};
      (updatedCustomer?.rateOverrides || []).forEach((o) => {
        if (o.rate) overrideMap[o.rate] = o.price;
      });
      setRateOverrideMap(overrideMap);

      setEditingRates(false);
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSavingRates(false);
    }
  };

  const uploadDocument = async (file, category = "supporting_document") => {
    try {
      setUploadingDoc(true);
      const fd = new FormData();
      fd.append("images", file);
      const res = await fetch(`${API_BASE}/upload/images`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      const url = await res.json();

      const newDoc = {
        name: file.name,
        url: typeof url === "string" ? url : "",
        type: file.type,
        category,
        uploadedAt: new Date(),
      };

      const updatedDocs = [...(customer.documents || []), newDoc];

      const saveRes = await fetch(`${CUSTOMERS_API}/${customer._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...customer, documents: updatedDocs }),
      });
      if (!saveRes.ok) throw new Error("Failed to save document");

      const updated = await saveRes.json();
      setCustomer(updated?.data || updated);
    } catch (err) {
      console.error(err);
      alert("Failed to upload document: " + err.message);
    } finally {
      setUploadingDoc(false);
    }
  };

  const removeDocument = async (index) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      const updatedDocs = (customer.documents || []).filter((_, i) => i !== index);
      const res = await fetch(`${CUSTOMERS_API}/${customer._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...customer, documents: updatedDocs }),
      });
      if (!res.ok) throw new Error("Failed to remove document");
      const updated = await res.json();
      setCustomer(updated?.data || updated);
    } catch (err) {
      console.error(err);
      alert("Failed to remove document: " + err.message);
    }
  };

  const generatePortalSetupLink = async () => {
    if (!customer?.email) {
      alert("This customer needs an email address before you can set up portal access.");
      return;
    }
    setCreatingPortalLink(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/customer-auth/staff/create-setup-link/${customer._id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json())?.message || "Failed to create setup link");
      const data = await res.json();
      setPortalSetupUrl(data.setupUrl);
    } catch (err) {
      alert(err.message);
    } finally {
      setCreatingPortalLink(false);
    }
  };

  const disablePortalAccess = async () => {
    if (!window.confirm("Revoke this customer's portal access? They'll need a new setup link to log in again.")) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/customer-auth/staff/disable/${customer._id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to disable portal access");
      setPortalSetupUrl("");
      await loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteCustomer = async () => {
    const confirmed = window.confirm(
      `Delete ${customer.companyName}? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      setDeletingCustomer(true);
      const res = await fetch(`${CUSTOMERS_API}/${customer._id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`Server responded ${res.status}`);
      navigate("/dashboard/customers");
    } catch (err) {
      console.error(err);
      alert("Failed to delete customer: " + err.message);
      setDeletingCustomer(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-6 space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-gray-600">Loading customer...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="w-full p-6 space-y-4">
        <Link
          to="/dashboard/customers"
          className="inline-flex rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          ← Back
        </Link>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          Customer not found.
        </div>
      </div>
    );
  }

  const customerId =
    customer.customerRef ||
    customer.customerId ||
    customer._id?.slice(-6).toUpperCase();

  const customerName =
    customer.companyName ||
    customer.tradingName ||
    customer.contactName ||
    "Unnamed Customer";

  return (
    <div className="w-full p-6 space-y-6">
      <div className="mb-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Link
                to="/dashboard/customers"
                className="inline-flex rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                ← Back
              </Link>

              <StatusBadge status={customer.status || "active"} />
              <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                {toTitle(customer.customerType || "general")}
              </span>
            </div>

            <h1 className="text-2xl font-bold text-gray-900">
              {customerName}
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              {customer.tradingName || customer.contactName || "No trading name"} ·{" "}
              {customer.phone || "-"}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleDeleteCustomer}
              disabled={deletingCustomer}
              className="rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-red-50 disabled:opacity-50"
            >
              {deletingCustomer ? "Deleting..." : "Delete Customer"}
            </button>

            <Link
              to={`/dashboard/bookings/new?customerId=${customer._id}`}
              className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              + New Booking
            </Link>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.65fr_0.85fr]">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
              <MetricCard label="Total Bookings" value={customerBookings.length} />
              <MetricCard label="Livestock Head (Total)" value={livestockSummary.totalHead.toLocaleString()} />
              <MetricCard label="Total Invoiced" value={`$${totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <MetricCard label="Total Paid" value={`$${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} highlight="green" />
              <MetricCard label="Outstanding" value={`$${totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} highlight={totalOutstanding > 0 ? "red" : null} />
              <MetricCard label="Unpaid Invoices" value={unpaidCount} highlight={unpaidCount > 0 ? "amber" : null} />
            </div>

            <SectionCard title="Contact Details">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                <Info label="Company Name" value={customer.companyName || "-"} />
                <Info label="Trading Name" value={customer.tradingName || "-"} />
                <Info label="Contact Name" value={customer.contactName || "-"} />
                <Info label="Email" value={customer.email || "-"} />
                <Info label="Phone" value={customer.phone || "-"} />
                <Info label="Billing Email" value={customer.billingEmail || "-"} />
                <Info
                  label="Customer Type"
                  value={toTitle(customer.customerType || "general")}
                />
                <Info
                  label="Status"
                  value={toTitle(customer.status || "active")}
                />
                <Info
                  label="Xero"
                  value={customer.xeroContactId ? "Connected" : "Not connected"}
                />
              </div>
            </SectionCard>

            {livestockSummary.consignmentCount > 0 && (
              <SectionCard title="Livestock Summary">
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 mb-5">
                  <Info label="Total Head Shipped" value={livestockSummary.totalHead.toLocaleString()} />
                  <Info label="Consignments" value={livestockSummary.consignmentCount} />
                  <Info label="Fit for Transport Declared" value={`${livestockSummary.fitCount}/${livestockSummary.consignmentCount}`} />
                  <Info label="Vet Certs Provided" value={`${livestockSummary.vetCertCount}/${livestockSummary.consignmentCount}`} />
                </div>
                <div className="space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Species Breakdown</div>
                  {livestockSummary.speciesBreakdown.map(([species, head]) => {
                    const pct = livestockSummary.totalHead > 0 ? Math.round((head / livestockSummary.totalHead) * 100) : 0;
                    return (
                      <div key={species}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-medium text-slate-700 capitalize">{species}</span>
                          <span className="text-slate-400">{head.toLocaleString()} head · {pct}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-2 bg-slate-800 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            <SectionCard title="Documents on File">
              <p className="text-sm text-slate-500 mb-4">
                Vet certificates, farmer declarations, or other documents kept on file for this customer so they don't need to be re-uploaded every booking.
              </p>
              <label className={`flex items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-6 cursor-pointer transition mb-4 ${uploadingDoc ? "border-slate-200 bg-slate-50" : "border-slate-300 hover:border-slate-400 hover:bg-slate-50"}`}>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx"
                  className="hidden"
                  disabled={uploadingDoc}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    for (const file of files) {
                      await uploadDocument(file);
                    }
                    e.target.value = "";
                  }}
                />
                <div className="text-center">
                  <div className="text-sm font-semibold text-slate-700">{uploadingDoc ? "Uploading..." : "Click to upload a document"}</div>
                  <div className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, Excel, Word</div>
                </div>
              </label>

              {(customer.documents || []).length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-4">No documents on file.</div>
              ) : (
                <div className="space-y-2">
                  {customer.documents.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="text-slate-400">📄</span>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{doc.name}</div>
                          <div className="text-xs text-slate-400 capitalize">{(doc.category || "document").replace(/_/g, " ")} · {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString("en-NZ") : ""}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.url && <a href={doc.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600 hover:underline">View</a>}
                        <button onClick={() => removeDocument(i)} className="text-xs font-semibold text-red-500 hover:underline">Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>


            <SectionCard title="Booking History">
              {customerBookings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-200 bg-slate-50">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-semibold text-slate-700">
                          Ref
                        </th>
                        <th className="px-4 py-3 font-semibold text-slate-700">
                          Requested Sailing
                        </th>
                        <th className="px-4 py-3 font-semibold text-slate-700">
                          Status
                        </th>
                        <th className="px-4 py-3 font-semibold text-slate-700">
                          Revenue
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-700">
                          View
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {customerBookings.map((booking) => {
                        const bookingRef =
                          booking.bookingRef ||
                          booking.ref ||
                          booking._id?.slice(-6).toUpperCase();

                        const revenue = Number(
                          booking.pricingSnapshot?.calculatedTotal ||
                            booking.totals?.estimatedRevenue ||
                            0
                        );

                        return (
                          <tr
                            key={booking._id || bookingRef}
                            className="border-t border-slate-200 hover:bg-slate-50"
                          >
                            <td className="px-4 py-3 font-medium text-slate-900">
                              {bookingRef}
                            </td>

                            <td className="px-4 py-3">
                              {booking.requestedSailingRef ||
                                booking.sailingSnapshot?.route ||
                                booking.route ||
                                "Unassigned"}
                            </td>

                            <td className="px-4 py-3">
                              <StatusBadge status={booking.status || "pending"} />
                            </td>

                            <td className="px-4 py-3 font-semibold">
                              ${revenue.toLocaleString()}
                            </td>

                            <td className="px-4 py-3 text-right">
                              <Link
                                to={`/dashboard/bookings/${booking._id}`}
                                className="inline-block rounded-lg border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-100"
                              >
                                Open
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-slate-500">
                  No bookings linked yet.
                </div>
              )}
            </SectionCard>

<SectionCard title="Invoice History">
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="border-b border-slate-200 bg-slate-50">
        <tr className="text-left">
          <th className="px-4 py-3">Invoice</th>
          <th className="px-4 py-3">Booking</th>
          <th className="px-4 py-3">Status</th>
          <th className="px-4 py-3">Amount</th>
        </tr>
      </thead>

      <tbody>
        {customerBookings.filter(
          (b) => b.invoice?.invoiceNumber
        ).length > 0 ? (
          customerBookings
            .filter((b) => b.invoice?.invoiceNumber)
            .map((booking) => (
              <tr
                key={booking._id}
                className="border-t border-slate-200 hover:bg-slate-50"
              >
                <td className="px-4 py-3 font-semibold">
                  {booking.invoice?.invoiceNumber}
                </td>

                <td className="px-4 py-3">
                  {booking.bookingRef ||
                    booking.ref ||
                    booking._id?.slice(-6).toUpperCase()}
                </td>

                <td className="px-4 py-3">
                  <StatusBadge
                    status={
                      booking.invoice?.invoiceStatus ||
                      "Draft"
                    }
                  />
                </td>

                <td className="px-4 py-3 font-semibold">
                  $
                  {Number(
                    booking.charges?.total || 0
                  ).toLocaleString()}
                </td>
              </tr>
            ))
        ) : (
          <tr>
            <td
              colSpan="4"
              className="px-4 py-8 text-center text-slate-500"
            >
              No invoices created yet.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
</SectionCard>

            <SectionCard title="Customer Portal Access">
              <p className="text-sm text-slate-500 mb-4">
                Let this customer log in to submit and track their own booking requests.
              </p>

              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 mb-4">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Portal Access: {customer.portalLogin?.loginEnabled ? "Enabled" : "Not Enabled"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {customer.portalLogin?.lastLoginAt
                      ? `Last login: ${new Date(customer.portalLogin.lastLoginAt).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}`
                      : customer.portalLogin?.passwordSetAt
                      ? "Account set up, never logged in"
                      : "No password set yet"}
                  </div>
                </div>
                {customer.portalLogin?.loginEnabled && (
                  <button onClick={disablePortalAccess} className="text-xs font-semibold text-red-500 hover:underline">
                    Revoke Access
                  </button>
                )}
              </div>

              <button
                onClick={generatePortalSetupLink}
                disabled={creatingPortalLink}
                className="w-full rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {creatingPortalLink ? "Generating..." : customer.portalLogin?.passwordSetAt ? "Generate New Setup Link" : "Generate Setup Link"}
              </button>

              {portalSetupUrl && (
                <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
                  <div className="text-xs font-semibold text-blue-800 mb-2">
                    Send this link to {customer.email} — it expires in 48 hours.
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={portalSetupUrl}
                      className="flex-1 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs text-slate-700"
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(portalSetupUrl); }}
                      className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </SectionCard>

            <SectionCard title="Customer Rates">
              <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Commercial Rate Card
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Every master rate is listed below. Only rows you give a different price get saved as an override for this customer — everything else automatically uses the standard rate.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {Object.keys(rateOverrideMap).length > 0
                      ? `${Object.keys(rateOverrideMap).length} custom rate${Object.keys(rateOverrideMap).length > 1 ? "s" : ""}`
                      : "Standard rates"}
                  </span>

                  {!editingRates ? (
                    <button
                      type="button"
                      onClick={() => setEditingRates(true)}
                      className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
                    >
                      Edit Rates
                    </button>
                  ) : (
                    <>
                      {Object.keys(rateOverrideMap).length > 0 && (
                        <button
                          type="button"
                          onClick={resetAllRateOverrides}
                          className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                        >
                          Reset All to Standard
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const overrideMap = {};
                          (customer?.rateOverrides || []).forEach((o) => {
                            if (o.rate) overrideMap[o.rate] = o.price;
                          });
                          setRateOverrideMap(overrideMap);
                          setEditingRates(false);
                        }}
                        className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        onClick={saveRates}
                        disabled={savingRates}
                        className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
                      >
                        {savingRates ? "Saving..." : "Save Rates"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {(() => {
                const activeRates = standardRates.filter((r) => r.active !== false);
                const visibleRates = activeRates.filter((r) => !hiddenRateIds.has(r._id));
                const hiddenRates = activeRates.filter((r) => hiddenRateIds.has(r._id));

                if (activeRates.length === 0) {
                  return (
                    <p className="text-sm text-slate-500">
                      No master rates set up yet — add some in Settings → Rates first.
                    </p>
                  );
                }

                return (
                  <>
                    {selectedRateIds.size > 0 && (
                      <div className="mb-3 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5">
                        <span className="text-xs font-semibold text-slate-600">
                          {selectedRateIds.size} selected
                        </span>
                        <button
                          type="button"
                          onClick={hideSelectedRates}
                          disabled={savingHidden}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                        >
                          {savingHidden ? "Hiding..." : `Hide Selected (${selectedRateIds.size})`}
                        </button>
                      </div>
                    )}

                    {visibleRates.length === 0 ? (
                      <p className="text-sm text-slate-500">
                        All master rates are hidden for this customer.
                      </p>
                    ) : (
                      <div className="overflow-x-auto rounded-2xl border border-slate-200">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left">
                              <th className="px-4 py-3 w-10">
                                <input
                                  type="checkbox"
                                  checked={visibleRates.length > 0 && visibleRates.every((r) => selectedRateIds.has(r._id))}
                                  onChange={() => {
                                    setSelectedRateIds((prev) => {
                                      const allSelected = visibleRates.every((r) => prev.has(r._id));
                                      if (allSelected) return new Set();
                                      return new Set(visibleRates.map((r) => r._id));
                                    });
                                  }}
                                  className="h-4 w-4 rounded border-slate-300"
                                />
                              </th>
                              <th className="px-4 py-3 font-semibold text-slate-700">Name</th>
                              <th className="px-4 py-3 font-semibold text-slate-700">Type</th>
                              <th className="px-4 py-3 font-semibold text-slate-700">Route</th>
                              <th className="px-4 py-3 font-semibold text-slate-700">Unit</th>
                              <th className="px-4 py-3 font-semibold text-slate-700 text-right">Standard Price</th>
                              <th className="px-4 py-3 font-semibold text-slate-700 text-right">Customer Price</th>
                              {editingRates && <th className="px-4 py-3 w-10"></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRates.map((rate, i) => {
                              const isOverridden = rateOverrideMap[rate._id] != null;
                              const effectivePrice = isOverridden ? rateOverrideMap[rate._id] : Number(rate.price || 0);
                              return (
                                <tr key={rate._id} className={`border-t border-slate-100 ${isOverridden ? "bg-amber-50" : i % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                                  <td className="px-4 py-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedRateIds.has(rate._id)}
                                      onChange={() => toggleRateSelected(rate._id)}
                                      className="h-4 w-4 rounded border-slate-300"
                                    />
                                  </td>
                                  <td className="px-4 py-3 font-medium text-slate-900">{rate.name}</td>
                                  <td className="px-4 py-3 text-slate-600">{toTitle(rate.type)}</td>
                                  <td className="px-4 py-3 text-slate-600">{rate.route || "Any"}</td>
                                  <td className="px-4 py-3 text-slate-600">{toTitle(rate.unit)}</td>
                                  <td className="px-4 py-3 text-right text-slate-500">${Number(rate.price || 0).toLocaleString()}</td>
                                  <td className="px-4 py-3 text-right">
                                    {editingRates ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder={String(rate.price || 0)}
                                        value={rateOverrideMap[rate._id] ?? ""}
                                        onChange={(e) => updateRateOverride(rate._id, e.target.value)}
                                        className="input w-28 text-right"
                                      />
                                    ) : (
                                      <span className={`font-semibold ${isOverridden ? "text-amber-700" : "text-slate-900"}`}>
                                        ${effectivePrice.toLocaleString()}
                                      </span>
                                    )}
                                  </td>
                                  {editingRates && (
                                    <td className="px-4 py-3 text-right">
                                      {isOverridden && (
                                        <button
                                          type="button"
                                          onClick={() => resetRateOverride(rate._id)}
                                          title="Reset to standard price"
                                          className="text-xs font-semibold text-slate-400 hover:text-red-500"
                                        >
                                          ✕
                                        </button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {hiddenRates.length > 0 && (
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => setShowHidden((v) => !v)}
                          className="text-xs font-semibold text-slate-400 hover:text-slate-700"
                        >
                          {showHidden ? "▲ Hide" : "▼ Show"} {hiddenRates.length} hidden rate{hiddenRates.length > 1 ? "s" : ""}
                        </button>

                        {showHidden && (
                          <div className="mt-3 space-y-2">
                            {hiddenRates.map((rate) => (
                              <div key={rate._id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5">
                                <div className="text-sm text-slate-500">
                                  <span className="font-medium text-slate-700">{rate.name}</span>
                                  {" · "}{toTitle(rate.type)} · ${Number(rate.price || 0).toLocaleString()}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => unhideRate(rate._id)}
                                  disabled={savingHidden}
                                  className="text-xs font-semibold text-slate-600 hover:text-slate-950 disabled:opacity-50"
                                >
                                  Unhide
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </SectionCard>

          </div>

          <div className="space-y-6">
            <AsideCard title="Customer Summary">
              <SummaryRow label="Customer ID" value={customerId} />
              <SummaryRow label="Company Name" value={customer.companyName || "-"} />
              <SummaryRow label="Trading Name" value={customer.tradingName || "-"} />
              <SummaryRow
                label="Contact"
                value={customer.contactName || "-"}
              />
              <SummaryRow label="Email" value={customer.email || "-"} />
              <SummaryRow label="Phone" value={customer.phone || "-"} />
            </AsideCard>

            <AsideCard title="Financial Snapshot">
              <SummaryRow label="Customer ID" value={customerId} />
              <SummaryRow label="Bookings" value={String(customerBookings.length)} />
              <SummaryRow label="Total Invoiced" value={`$${totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <SummaryRow label="Total Paid" value={`$${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <SummaryRow label="Outstanding" value={`$${totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <SummaryRow label="Unpaid Invoices" value={String(unpaidCount)} />
              <SummaryRow label="Rate Source" value={Object.keys(rateOverrideMap).length > 0 ? "Custom" : "Standard"} />
              <SummaryRow label="Xero" value={customer.xeroContactId ? "Connected" : "Not connected"} />
              <SummaryRow label="Moneyworks Code" value={customer.moneyworksCode || "Not set — auto-generates on first invoice"} />
            </AsideCard>
          </div>
        </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.9rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0.75rem 0.95rem;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .input::placeholder {
          color: rgb(148 163 184);
        }

        .input:hover {
          border-color: rgb(148 163 184);
        }

        .input:focus {
          border-color: rgb(15 23 42);
          box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08);
        }
      `}</style>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function AsideCard({ title, children }) {
  return (
    <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </h2>
      <div className="mt-4 space-y-3">{children}</div>
    </aside>
  );
}

function MetricCard({ label, value, highlight }) {
  const valueColor =
    highlight === "green" ? "text-emerald-600" :
    highlight === "red" ? "text-red-600" :
    highlight === "amber" ? "text-amber-600" :
    "text-slate-900";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`mt-2 text-2xl font-bold ${valueColor}`}>{value}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-slate-900">{value || "-"}</p>
    </div>
  );
}

function RateBox({ title, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
      <h3 className="mb-4 font-semibold text-slate-900">{title}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function RateField({ label, value, editing, onChange }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </label>

      {editing ? (
        <input
          type="number"
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900">
          ${Number(value || 0).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className="max-w-[60%] break-words text-right text-sm font-semibold text-slate-900">
        {value}
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const clean = String(status || "active").toLowerCase();

  const styles =
    clean === "active"
      ? "bg-emerald-100 text-emerald-700"
      : clean === "inactive"
      ? "bg-slate-100 text-slate-700"
      : clean === "suspended"
      ? "bg-red-100 text-red-700"
      : clean === "confirmed"
      ? "bg-green-100 text-green-700"
      : clean === "invoiced"
      ? "bg-blue-100 text-blue-700"
      : clean === "completed"
      ? "bg-slate-900 text-white"
      : clean === "cancelled"
      ? "bg-red-100 text-red-700"
      : "bg-amber-100 text-amber-700";

  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${styles}`}
    >
      {toTitle(clean)}
    </span>
  );
}

function toTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}